<script setup>
import { computed, ref, watch } from 'vue'
import { useUiStore } from '@/stores/UiStore'
import { useLoadsStore } from '@/stores/Loads'
import { useToastStore } from '@/stores/Toast'
import { DELETE_LOAD, VIEW_LOAD } from '@/constants/dispatch'
import ConfirmationDialog from '@/components/shared/ConfirmationDialog.vue'

/**
 * The local refs.
 */
const show = ref(false)
const isLoading = ref(false)

/**
 * The global stores.
 */
const uiStore = useUiStore()
const store = useLoadsStore()
const toastStore = useToastStore()

/**
 * Load data.
 *
 * @return {Object}
 */
const data = computed(() => {
    return uiStore.actionLoads(DELETE_LOAD)
})

/**
 * The load id.
 *
 * @return {String}
 */
const id = computed(() => {
    return data.value?.id || ''
})

/**
 * Determine if the action is active.
 *
 * @return {Boolean}
 */
const isActive = computed(() => {
    return uiStore.isActionActive(DELETE_LOAD)
})

/**
 * Handle the delete click event.
 *
 * @return {void}
 */
const onDelete = async () => {
    try {
        isLoading.value = true

        uiStore.closeAction(VIEW_LOAD)

        await store.delete(data.value.id)

        toastStore.setNotification({
            message: `Load has been deleted.`,
            type: 'success',
        })
    } catch (error) {
        toastStore.setNotification({
            message: error.message(),
            type: 'error',
        })
    }

    isLoading.value = false

    uiStore.closeAction(DELETE_LOAD)
}

/**
 * Watch the isActive prop.
 */
watch(
    isActive,
    (value) => {
        show.value = value
    },
    { immediate: true }
)

/**
 * Watch the show ref.
 */
watch(show, (value) => {
    if (!value) {
        return uiStore.closeAction(DELETE_LOAD)
    }
})
</script>

<template>
    <confirmation-dialog
        v-model="show"
        @confirm="onDelete"
        :is-loading="isLoading"
        class="!rounded-lg"
    >
        <template #title>
            <div class="text-lg font-bold">Confirmation</div>
        </template>
        <template #body>
            <div class="text-sm">
                Are you sure you want to delete load with PRO #{{ id }}?
            </div>
        </template>
    </confirmation-dialog>
</template>
